import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import React, { ReactNode, useState } from 'react'
import ReactDOM from 'react-dom'
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, PlusIcon } from '../../icons'
import { Button } from './Button'

export interface EditableListProps<T> {
    items: T[]
    onItemsChange: (items: T[]) => void
    getItemId: (item: T, index: number) => string
    createNewItem: () => T
    renderItem: (item: T, index: number) => ReactNode
    renderHeader?: (item: T, index: number) => ReactNode
    itemLabel?: string
    addButtonLabel?: string
    emptyMessage?: string
    reorderable?: boolean
    collapsible?: boolean
    startCollapsed?: boolean
    maxHeight?: string
    className?: string
}

export const EditableList = <T,>(props: EditableListProps<T>) => {
    const {
        items,
        onItemsChange,
        getItemId,
        createNewItem,
        renderItem,
        renderHeader,
        itemLabel = 'Item',
        addButtonLabel = 'Add Item',
        emptyMessage = 'No items yet',
        reorderable = true,
        collapsible = true,
        startCollapsed = true,
        maxHeight = '400px',
        className = '',
    } = props

    console.log('Rendering EditableList with items:', items)

    const [collapseToggledItems, setCollapseToggledItems] = useState<Set<string>>(new Set())
    const [isDragging, setIsDragging] = useState(false)

    const handleAddItem = () => {
        const newItem = createNewItem()
        onItemsChange([...items, newItem])
        if (startCollapsed) {
            const newCollapseToggled = new Set(collapseToggledItems)
            newCollapseToggled.add(getItemId(newItem, items.length))
            setCollapseToggledItems(newCollapseToggled)
        }
    }

    const handleDragStart = () => {
        setIsDragging(true)
    }

    const handleDragEnd = (result: DropResult) => {
        setIsDragging(false)

        if (!result.destination) return

        const reorderedItems = Array.from(items)
        const [movedItem] = reorderedItems.splice(result.source.index, 1)
        reorderedItems.splice(result.destination.index, 0, movedItem)

        onItemsChange(reorderedItems)
    }

    const handleRemoveItem = (index: number) => {
        const itemId = getItemId(items[index], index)
        const newItems = items.filter((_, i) => i !== index)
        onItemsChange(newItems)

        // Clean up collapsed state
        const newCollapseToggled = new Set(collapseToggledItems)
        newCollapseToggled.delete(itemId)
        setCollapseToggledItems(newCollapseToggled)
    }

    const toggleItemCollapse = (index: number) => {
        const itemId = getItemId(items[index], index)
        const newCollapseToggled = new Set(collapseToggledItems)
        if (newCollapseToggled.has(itemId)) {
            newCollapseToggled.delete(itemId)
        } else {
            newCollapseToggled.add(itemId)
        }
        setCollapseToggledItems(newCollapseToggled)
    }

    const isCollapsed = (itemId: string) => collapseToggledItems.has(itemId) !== startCollapsed

    const renderItemWrapper = (item: T, index: number, provided: any, snapshot: {isDragging: boolean}) => {
        const itemId = getItemId(item, index)
        const child = (
            <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                className={`border border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-3 ${
                    snapshot.isDragging
                        ? 'shadow-2xl ring-2 ring-primary-500 bg-white dark:bg-gray-800 z-50'
                        : 'bg-gray-50 dark:bg-gray-800/50'
                }`}
                style={{
                    ...provided.draggableProps.style,
                    ...(snapshot.isDragging
                        ? {
                              transform: provided.draggableProps.style?.transform,
                          }
                        : {}),
                }}
            >
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-1">
                        {reorderable && (
                            <div
                                {...provided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                            >
                                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                </svg>
                            </div>
                        )}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {renderHeader ? (
                                renderHeader(item, index)
                            ) : (
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {itemLabel} {index + 1}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {collapsible && (
                            <Button
                                type="button"
                                onClick={() => toggleItemCollapse(index)}
                                variant="icon"
                                color="neutral"
                                size="sm"
                            >
                                {isCollapsed(itemId) ? (
                                    <ChevronDownIcon className="w-4 h-4" />
                                ) : (
                                    <ChevronUpIcon className="w-4 h-4" />
                                )}
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            variant="icon"
                            color="danger"
                            size="sm"
                        >
                            <DeleteIcon className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {(!collapsible || !isCollapsed(itemId)) && <div>{renderItem(item, index)}</div>}
            </div>
        )

        // Use portal when dragging to escape parent overflow constraints
        if (snapshot.isDragging) {
            return ReactDOM.createPortal(child, document.body)
        }
        return child
    }

    const renderList = () => {
        if (!reorderable) {
            // Non-draggable list
            return (
                <div className={`space-y-4 pe-2`} style={{ maxHeight, overflowY: 'auto' }}>
                    {items.map((item, index) => (
                        <div key={getItemId(item, index)}>
                            {renderItemWrapper(
                                item,
                                index,
                                { innerRef: null, draggableProps: { style: {} }, dragHandleProps: {} },
                                { isDragging: false }
                            )}
                        </div>
                    ))}
                </div>
            )
        }

        // Draggable list
        return (
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <Droppable droppableId="editable-list">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{ maxHeight, overflowY: 'auto', position: 'relative' }}
                        >
                            {items.map((item, index) => (
                                <Draggable key={getItemId(item, index)} draggableId={getItemId(item, index)} index={index}>
                                    {(provided, snapshot) => renderItemWrapper(item, index, provided, snapshot)}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        )
    }

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{itemLabel}s</label>
                <Button type="button" onClick={handleAddItem} variant="outline" size="sm">
                    <PlusIcon className="w-4 h-4" />
                    {addButtonLabel}
                </Button>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
                </div>
            ) : (
                renderList()
            )}
        </div>
    )
}

EditableList.displayName = 'EditableList'
